/**
 * 고정(sticky) 스크롤 연출의 계산부 — 홈·교육 히어로의 카드 펼치기가 같이 쓴다.
 * DOM 은 `use-sticky-scroll` 이 만진다. 여기는 숫자만 다룬다.
 *
 * 고정 구간의 진행도 0→1 을 두 단계로 나눈다.
 *   1) 펼치기(0 → spreadEnd): 모여 있던(stacked) 카드가 제자리(spread)로 벌어진다.
 *   2) 떠오르기(spreadEnd → 1): 펼친 카드가 마지막 자세(drift)로 흐른다.
 *      멈춰 두고 싶으면 drift 를 spread 와 같게 두면 된다.
 *
 * 이동량은 카드 자기 크기의 % 로 적는다 — 뷰포트 폭이 바뀌어도 간격 비율이 유지된다.
 */

export type Pose = {
  /** 카드 폭 대비 가로 이동(%) */
  readonly x: number;
  /** 카드 높이 대비 세로 이동(%) */
  readonly y: number;
  /** 회전(deg) — 양수가 시계 방향 */
  readonly rotate: number;
  readonly scale: number;
};

export type PoseTrack = {
  readonly stacked: Pose;
  readonly spread: Pose;
  readonly drift: Pose;
};

export type StagePhases = { readonly spread: number; readonly drift: number };

/** 기본값: 진행도의 앞 절반은 펼치기, 뒤 절반은 떠오르기 */
export const DEFAULT_SPREAD_END = 0.5;

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

const easeInOutCubic = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

const easeOutQuad = (t: number): number => 1 - (1 - t) * (1 - t);

const lerp = (from: number, to: number, t: number): number => from + (to - from) * t;

const lerpPose = (from: Pose, to: Pose, t: number): Pose => ({
  x: lerp(from.x, to.x, t),
  y: lerp(from.y, to.y, t),
  rotate: lerp(from.rotate, to.rotate, t),
  scale: lerp(from.scale, to.scale, t),
});

/**
 * 고정 구간 안에서의 진행도.
 *
 * 프레임은 `stickyTop`(헤더 높이)에 붙어 `viewportHeight - stickyTop` 만큼 차지한다.
 * 섹션이 그보다 긴 만큼이 스크롤로 소비할 거리다.
 */
export function scrollProgress(
  rect: { readonly top: number; readonly height: number },
  viewportHeight: number,
  stickyTop: number,
): number {
  const distance = rect.height - (viewportHeight - stickyTop);
  if (distance <= 0) return 0;
  return clamp01((stickyTop - rect.top) / distance);
}

export function stagePhases(progress: number, spreadEnd = DEFAULT_SPREAD_END): StagePhases {
  const p = clamp01(progress);
  return {
    spread: easeInOutCubic(clamp01(p / spreadEnd)),
    drift: easeOutQuad(clamp01((p - spreadEnd) / (1 - spreadEnd))),
  };
}

export function trackPose(
  track: PoseTrack,
  progress: number,
  spreadEnd = DEFAULT_SPREAD_END,
): Pose {
  const { spread, drift } = stagePhases(progress, spreadEnd);
  if (drift > 0) return lerpPose(track.spread, track.drift, drift);
  return lerpPose(track.stacked, track.spread, spread);
}

/** 절대 위치로 가운데에 놓인 카드를 기준점으로 되돌린 뒤 자세를 적용한다 */
export function poseToTransform({ x, y, rotate, scale }: Pose): string {
  return `translate(-50%, -50%) translate(${x}%, ${y}%) rotate(${rotate}deg) scale(${scale})`;
}

/** DOM 의 data 속성처럼 밖에서 들어온 문자열을 궤적 표의 자리 이름으로 좁힌다 */
export function isTrackSlot<Slot extends string>(
  tracks: Readonly<Record<Slot, PoseTrack>>,
  value: string | undefined,
): value is Slot {
  return value !== undefined && Object.hasOwn(tracks, value);
}
